/**
 * Ahrefs Response Transformers (Story 20.1, AC #4)
 *
 * Maps raw Ahrefs API JSON responses into ProvenLeap's `AdapterMetrics.records[]`
 * shape. Every record carries:
 *
 *  - `_reportType` — discriminator identifying which endpoint produced the row.
 *    Mirrors PageSpeed's `_reportType: "lighthouse" | "cruxUrl" | "cruxOrigin"`
 *    pattern. Widgets read this field to render the correct view.
 *  - `target` — the domain/URL queried. Same value as `ctx.accountId`.
 *
 * **CRITICAL — currency divisor (cents → dollars):** Ahrefs returns currency
 * fields (`org_cost`, `paid_cost`, `cpc`, `traffic_value`) as integer USD CENTS.
 * The Integration Mapping Reference labels them as Currency (USD). Widgets
 * expect dollar values. The Amazon Ads `NO currency divisor` invariant from
 * Story 19.17 does NOT apply here — Amazon was already in dollars; Ahrefs is
 * in cents. We divide by 100 with rounding to preserve 2 decimal places.
 *
 * Tests in `transformers.test.ts` enforce: `transformAhrefsCurrencyField(1234) === 12.34`,
 * `transformAhrefsCurrencyField(0) === 0`, `transformAhrefsCurrencyField(null) === null`.
 */

import type { AhrefsMode } from "./normalize";

// ============================================================================
// Currency conversion (AC #4)
// ============================================================================

/**
 * Convert an Ahrefs cents value to USD dollars with 2-decimal precision.
 *
 *  - `null`/`undefined` → `null` (preserve missing-field semantics)
 *  - `0` → `0`
 *  - `1234` → `12.34`
 *
 * Rounding: `Math.round(v * 100) / 100` after the cents divide ensures float
 * artifacts like `12.339999999998` stay clean. Negative values are passed
 * through (Ahrefs occasionally returns negative `traffic_value` on penalized
 * domains — preserving the sign is correct).
 */
export function transformAhrefsCurrencyField(
  cents: number | null | undefined
): number | null {
  if (cents === null || cents === undefined) return null;
  if (!Number.isFinite(cents)) return null;
  const dollars = cents / 100;
  return Math.round(dollars * 100) / 100;
}

// ============================================================================
// Discriminator string literal (Story 20.1)
// ============================================================================

export type AhrefsReportType =
  | "domain-rating"
  | "backlinks-stats"
  | "metrics"
  | "batch-analysis"
  // Story 20.2 — 4 new endpoint discriminators
  | "keywords-explorer-overview"
  | "organic-keywords"
  | "rank-tracker-competitors-stats"
  | "keywords-history"
  // Story 20.2a — time-series + per-country breakdowns
  | "metrics-history"
  | "metrics-by-country"
  // Story 20.2c — Rank Tracker per-keyword `/overview`
  | "rank-tracker-overview"
  // Story 20.2d — Site Audit (4 endpoints)
  | "site-audit-projects"
  | "site-audit-issues"
  | "site-audit-page-explorer"
  | "site-audit-page-content"
  // Story 20.2e — Brand Radar (8 GET endpoints; 9 discriminators because
  // /sov-overview + /sov-history are 2 distinct surfaces with different
  // response shapes — overview is `{ brand, share_of_voice }` per row;
  // history is per-date with a nested `share_of_voice: Array<{brand, value}>`).
  | "brand-radar-ai-responses"
  | "brand-radar-impressions-history"
  | "brand-radar-mentions-history"
  | "brand-radar-impressions-overview"
  | "brand-radar-mentions-overview"
  | "brand-radar-sov-overview"
  | "brand-radar-sov-history"
  | "brand-radar-cited-domains"
  | "brand-radar-cited-pages"
  // Story 20.2f — Web Analytics overview surface (`/stats` + `/chart`). Per
  // docs verification 2026-05-06, /stats returns a single aggregated object
  // (singleton) and /chart returns an array of per-timestamp points sharing
  // the same 6 fields plus `timestamp` (ISO 8601 string).
  | "web-analytics-stats"
  | "web-analytics-chart"
  // Story 35.8 — Subscription Info `/limits-and-usage` (FREE endpoint).
  // Singleton response shape (NOT an array): 7 fields per
  // docs.ahrefs.com/api/reference/subscription-info/get-limits-and-usage
  // verified 2026-05-19. Agency-facing API-budget telemetry surface.
  | "subscription-info-limits-and-usage";

// ============================================================================
// Endpoint-specific raw response shapes (loosely typed — defensive)
// ============================================================================

interface DomainRatingRaw {
  readonly domain_rating?: number | null;
  readonly ahrefs_rank?: number | null;
}

interface BacklinksStatsRaw {
  readonly live?: number | null;
  readonly all_time?: number | null;
  readonly live_refdomains?: number | null;
  readonly all_time_refdomains?: number | null;
}

interface MetricsRaw {
  readonly org_keywords?: number | null;
  readonly paid_keywords?: number | null;
  readonly org_keywords_1_3?: number | null;
  readonly org_traffic?: number | null;
  readonly org_cost?: number | null; // cents
  readonly paid_traffic?: number | null;
  readonly paid_cost?: number | null; // cents
  readonly paid_pages?: number | null;
}

interface BatchAnalysisRowRaw {
  readonly target?: string;
  readonly mode?: string;
  readonly domain_rating?: number | null;
  readonly url_rating?: number | null;
  readonly backlinks?: number | null;
  readonly backlinks_dofollow?: number | null;
  readonly backlinks_nofollow?: number | null;
  readonly refdomains?: number | null;
  readonly refdomains_dofollow?: number | null;
  readonly refdomains_nofollow?: number | null;
  readonly org_traffic?: number | null;
  readonly org_keywords?: number | null;
  readonly org_keywords_1_3?: number | null;
  readonly org_keywords_4_10?: number | null;
  readonly paid_traffic?: number | null;
  readonly paid_keywords?: number | null;
  readonly refips?: number | null;
  readonly refips_subnets?: number | null;
  readonly linked_domains?: number | null;
  readonly outgoing_links?: number | null;
  // Story 20.2b — +15 fields from the widened default select.
  readonly backlinks_internal?: number | null;
  readonly backlinks_redirect?: number | null;
  readonly backlinks_text?: number | null;
  readonly backlinks_image?: number | null;
  readonly backlinks_governmental?: number | null;
  readonly backlinks_educational?: number | null;
  readonly refdomains_governmental?: number | null;
  readonly refdomains_educational?: number | null;
  readonly refclass_c_subnets?: number | null;
  readonly linked_domains_dofollow?: number | null;
  readonly linked_domains_nofollow?: number | null;
  readonly outgoing_links_dofollow?: number | null;
  readonly outgoing_links_nofollow?: number | null;
  readonly crawled_pages?: number | null;
  readonly live_pages?: number | null;
  // Story 35.8 — Batch Analysis residual long-tail fields (catalogue rows
  // 29, 37, 40, 41, 48, 51, 52 per ahrefs.md). `org_traffic` is already
  // declared above (Story 20.1 baseline); the catalogue row 40 corresponds
  // to the SAME wire field but a different MetricDefinition surface
  // (synthetic key `batchOrganicTraffic` vs Story 20.1's `organicTraffic`).
  readonly ahrefs_rank?: number | null;
  readonly ip?: string | null;
  readonly org_cost?: number | null; // cents
  readonly org_traffic_top_by_country?: ReadonlyArray<{
    readonly country: string;
    readonly traffic: number;
  }> | null;
  readonly paid_ads?: number | null;
  readonly paid_cost?: number | null; // cents
}

// ============================================================================
// Story 20.2b — NaN-safe ratio helper for synthetic batch-analysis metrics
// ============================================================================

/**
 * Compute `numerator / divisor` with strict null-on-zero / null-on-missing
 * semantics. Required by Story 20.2b AC #3 — divide-by-zero would otherwise
 * emit `Infinity` / `NaN` to widgets and crash chart rendering.
 *
 *  - any input is null/undefined → null
 *  - divisor is 0 → null
 *  - either input is non-finite (NaN, ±Infinity) → null
 *  - else → ratio rounded to 6 decimals (preserves sub-percent precision —
 *    e.g. an enterprise site with 12 .gov refdomains out of 100,000 dofollow
 *    refdomains has a 0.00012 (0.012%) ratio, which 4-decimal rounding would
 *    silently truncate to 0.0001. Six decimals keeps that signal visible
 *    while still cleaning float artifacts.)
 *
 * Exported for test coverage; consumers in this file use it directly.
 */
export function safeRatio(
  numerator: number | null | undefined,
  divisor: number | null | undefined
): number | null {
  if (numerator === null || numerator === undefined) return null;
  if (divisor === null || divisor === undefined) return null;
  if (!Number.isFinite(numerator) || !Number.isFinite(divisor)) return null;
  if (divisor === 0) return null;
  const ratio = numerator / divisor;
  if (!Number.isFinite(ratio)) return null;
  return Math.round(ratio * 1_000_000) / 1_000_000;
}

// ============================================================================
// Transformers — one per endpoint
// ============================================================================

/**
 * Transform `/site-explorer/domain-rating` response to a ProvenLeap record.
 *
 * Ahrefs returns: `{ "domain_rating": 73, "ahrefs_rank": 42135 }`
 *
 * Output keys use camelCase (`domainRating`, `ahrefsRank`) to match the
 * `MetricDefinition.key` values — this is the contract widgets bind against.
 */
export function transformAhrefsDomainRatingResponse(
  raw: DomainRatingRaw,
  target: string,
  mode: AhrefsMode
): Record<string, unknown> {
  return {
    _reportType: "domain-rating" satisfies AhrefsReportType,
    target,
    mode,
    domainRating: raw.domain_rating ?? null,
    ahrefsRank: raw.ahrefs_rank ?? null,
  };
}

/**
 * Transform `/site-explorer/backlinks-stats` response.
 *
 * v3 returns ONLY 4 fields (`live`, `all_time`, `live_refdomains`,
 * `all_time_refdomains`) per `_bmad/bmm/data/integration-metrics/ahrefs.md`.
 * The dofollow/nofollow/UGC breakdown lives in `/batch-analysis` (Group D);
 * see AC #4 critical v2→v3 change documentation.
 */
export function transformAhrefsBacklinksStatsResponse(
  raw: BacklinksStatsRaw,
  target: string,
  mode: AhrefsMode
): Record<string, unknown> {
  return {
    _reportType: "backlinks-stats" satisfies AhrefsReportType,
    target,
    mode,
    liveBacklinks: raw.live ?? null,
    allTimeBacklinks: raw.all_time ?? null,
    liveReferringDomains: raw.live_refdomains ?? null,
    allTimeReferringDomains: raw.all_time_refdomains ?? null,
  };
}

/**
 * Transform `/site-explorer/metrics` response.
 *
 * Currency fields (`org_cost`, `paid_cost`) are converted from cents to
 * dollars per AC #4. Use `paidTrafficCost` as the camelCase key (matches
 * `AHREFS_METRICS` definition). `org_cost` is surfaced as
 * `organicTrafficValue` because that's the customer-facing label per the
 * Integration Mapping Reference.
 */
export function transformAhrefsMetricsResponse(
  raw: MetricsRaw,
  target: string,
  mode: AhrefsMode
): Record<string, unknown> {
  return {
    _reportType: "metrics" satisfies AhrefsReportType,
    target,
    mode,
    organicKeywords: raw.org_keywords ?? null,
    paidKeywords: raw.paid_keywords ?? null,
    organicKeywordsTop3: raw.org_keywords_1_3 ?? null,
    organicTraffic: raw.org_traffic ?? null,
    organicTrafficValue: transformAhrefsCurrencyField(raw.org_cost),
    paidTraffic: raw.paid_traffic ?? null,
    paidTrafficCost: transformAhrefsCurrencyField(raw.paid_cost),
    paidPages: raw.paid_pages ?? null,
  };
}

/**
 * Transform a single `/batch-analysis` row.
 *
 * Each row already includes `target` and `mode` from the request body, but
 * we also accept fallback values (the targetDefault/modeDefault arguments)
 * for defensive shape preservation if Ahrefs omits them.
 */
export function transformAhrefsBatchAnalysisRow(
  raw: BatchAnalysisRowRaw,
  targetDefault: string,
  modeDefault: AhrefsMode
): Record<string, unknown> {
  const target = typeof raw.target === "string" && raw.target.length > 0 ? raw.target : targetDefault;
  const mode =
    raw.mode === "domain" ||
    raw.mode === "subdomain" ||
    raw.mode === "prefix" ||
    raw.mode === "exact_url"
      ? raw.mode
      : modeDefault;

  return {
    _reportType: "batch-analysis" satisfies AhrefsReportType,
    target,
    mode,
    domainRating: raw.domain_rating ?? null,
    urlRating: raw.url_rating ?? null,
    liveBacklinks: raw.backlinks ?? null,
    backlinksDofollow: raw.backlinks_dofollow ?? null,
    backlinksNofollow: raw.backlinks_nofollow ?? null,
    liveReferringDomains: raw.refdomains ?? null,
    referringDomainsDofollow: raw.refdomains_dofollow ?? null,
    referringDomainsNofollow: raw.refdomains_nofollow ?? null,
    organicTraffic: raw.org_traffic ?? null,
    organicKeywords: raw.org_keywords ?? null,
    organicKeywordsTop3: raw.org_keywords_1_3 ?? null,
    organicKeywordsPositions4_10: raw.org_keywords_4_10 ?? null,
    paidTraffic: raw.paid_traffic ?? null,
    paidKeywords: raw.paid_keywords ?? null,
    referringIps: raw.refips ?? null,
    referringIpSubnets: raw.refips_subnets ?? null,
    linkedDomains: raw.linked_domains ?? null,
    outgoingLinks: raw.outgoing_links ?? null,
    // Story 20.2b — +13 new direct mirrors.
    internalBacklinks: raw.backlinks_internal ?? null,
    redirectBacklinks: raw.backlinks_redirect ?? null,
    backlinksText: raw.backlinks_text ?? null,
    backlinksImage: raw.backlinks_image ?? null,
    backlinksGovernmental: raw.backlinks_governmental ?? null,
    backlinksEducational: raw.backlinks_educational ?? null,
    referringDomainsGovernmental: raw.refdomains_governmental ?? null,
    referringDomainsEducational: raw.refdomains_educational ?? null,
    refClassCSubnets: raw.refclass_c_subnets ?? null,
    linkedDomainsDofollow: raw.linked_domains_dofollow ?? null,
    linkedDomainsNofollow: raw.linked_domains_nofollow ?? null,
    outgoingLinksDofollow: raw.outgoing_links_dofollow ?? null,
    outgoingLinksNofollow: raw.outgoing_links_nofollow ?? null,
    crawledPages: raw.crawled_pages ?? null,
    livePages: raw.live_pages ?? null,
    // Story 35.8 — Batch Analysis residual long-tail fields (catalogue rows
    // 29, 37, 40, 41, 48, 51, 52). Currency cents → dollars via
    // transformAhrefsCurrencyField for org_cost + paid_cost.
    // org_traffic_top_by_country ships as opaque-array (Array<{country,
    // traffic}>); per-element transformer deferred to Story 20.4.
    //
    // **Pass 2 fix (H2)** — emit BOTH (a) the snake_case wire field
    // preserved verbatim (cents for currency fields, NOT transformed) for
    // grep-friendly widget debugging AND (b) the `batch*` camelCase key
    // that matches the `MetricDefinition.key` in `AHREFS_METRICS`. Earlier
    // Pass 1 fix used `batchOrganicTrafficCost` etc. (the catalog key); but
    // the snake_case wire field was also being overwritten with transformed
    // dollars, which broke the "preserve wire-format verbatim" convention
    // asserted by the Subscription Info transformer test. Pass 2 keeps the
    // snake_case keys as wire-verbatim (cents) and the `batch*` keys as
    // transformed dollars.
    //
    // The `apiField` collision with Story 20.1's `organicTraffic` /
    // `organicTrafficValue` on `/site-explorer/metrics` (which emit camelCase
    // `organicTraffic` / `organicTrafficValue` directly on the row) is
    // resolved at the wire layer by the `_reportType` discriminator —
    // widgets keyed on `_reportType === "batch-analysis"` read
    // `batchOrganicTraffic` etc., widgets keyed on the metrics endpoint
    // read `organicTraffic`.
    ahrefs_rank: raw.ahrefs_rank ?? null,
    batchAhrefsRank: raw.ahrefs_rank ?? null,
    ip: raw.ip ?? null,
    batchIpAddress: raw.ip ?? null,
    // **Pass 3 fix (C1)** — align with the Story 20.2a convention:
    // `transformAhrefsMetricsHistoryRow` + `transformAhrefsMetricsByCountryRow`
    // BOTH emit transformed dollars under snake_case `org_cost`/`paid_cost`.
    // Pass 2 deliberately tried to preserve wire-verbatim cents on snake_case
    // keys here, but that diverged from the project-wide convention and
    // would silently break widgets that read `row.org_cost` after switching
    // between endpoints (`/metrics-history` vs `/batch-analysis`). The
    // dollars-under-snake_case convention is now uniform across all 3
    // transformers that emit `org_cost`/`paid_cost`. Widget config layers
    // can rely on `_reportType` discriminator + a single value semantic.
    org_cost: transformAhrefsCurrencyField(raw.org_cost),
    batchOrganicTrafficCost: transformAhrefsCurrencyField(raw.org_cost),
    // `org_traffic` shares the wire field with Story 20.1's `organicTraffic`
    // mirror emitted above; `batchOrganicTraffic` is the catalog key.
    batchOrganicTraffic: raw.org_traffic ?? null,
    org_traffic_top_by_country: raw.org_traffic_top_by_country ?? null,
    batchOrganicTrafficTopByCountry: raw.org_traffic_top_by_country ?? null,
    paid_ads: raw.paid_ads ?? null,
    batchPaidAdsCount: raw.paid_ads ?? null,
    paid_cost: transformAhrefsCurrencyField(raw.paid_cost),
    batchPaidCost: transformAhrefsCurrencyField(raw.paid_cost),
    // Story 20.2b — 3 synthetic ratio metrics (NaN-safe via safeRatio).
    // anchorDiversityRatio: text-anchor share of (text + image) anchors.
    // computeAnchorDiversityDivisor returns null when either input is
    // missing — preserve missing-data semantics by passing null through
    // safeRatio (which yields null when divisor is null).
    anchorDiversityRatio: safeRatio(
      raw.backlinks_text,
      computeAnchorDiversityDivisor(raw.backlinks_text, raw.backlinks_image)
    ),
    // educationalRefDomainPct: .edu share of dofollow refdomains.
    educationalRefDomainPct: safeRatio(
      raw.refdomains_educational,
      raw.refdomains_dofollow
    ),
    // governmentalRefDomainPct: .gov share of dofollow refdomains.
    governmentalRefDomainPct: safeRatio(
      raw.refdomains_governmental,
      raw.refdomains_dofollow
    ),
  };
}

/**
 * Story 20.2b — compute the divisor for `anchorDiversityRatio`. Returns
 * `null` when either anchor count is missing so safeRatio short-circuits to
 * null rather than silently treating "missing" as zero. Extracted as a named
 * helper to keep the call-site expression readable and testable.
 */
function computeAnchorDiversityDivisor(
  text: number | null | undefined,
  image: number | null | undefined
): number | null {
  if (text === null || text === undefined) return null;
  if (image === null || image === undefined) return null;
  return text + image;
}

/**
 * Transform an array of `/batch-analysis` rows. Convenience wrapper around
 * `transformAhrefsBatchAnalysisRow` for the common case.
 */
export function transformAhrefsBatchAnalysisResponse(
  raw: ReadonlyArray<BatchAnalysisRowRaw>,
  targetDefault: string,
  modeDefault: AhrefsMode
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsBatchAnalysisRow(row, targetDefault, modeDefault));
}

// ============================================================================
// Story 20.2 — Field-name normalization rule (AC #14)
// ============================================================================

/**
 * **Dual-naming rule documented at the metric definitions block in `ahrefs.ts`.**
 *
 * Ahrefs uses different wire-format field names for the same logical metric
 * across endpoints (per `_bmad/bmm/data/integration-metrics/ahrefs.md`):
 *
 *  - Keywords Explorer Overview: `difficulty` / `cpc` / `cps`
 *  - Rank Tracker per-row:       `keyword_difficulty` / `cost_per_click` /
 *                                `clicks_per_search`
 *  - Organic Keywords:           `keyword_difficulty` / `cpc`
 *
 * The transformers below ALWAYS preserve the raw wire-format field (so widget
 * config debugging stays grep-able against Ahrefs docs) AND emit synthetic
 * camelCase mirrors (`keywordDifficulty`, `costPerClick`, `clicksPerSearch`)
 * so the widget rendering layer can dispatch with a single key regardless of
 * which endpoint sourced the row.
 *
 * DO NOT pick one wire format and unify — that loses fidelity. The dual
 * representation is the canonical resolution.
 */

// ----------------------------------------------------------------------------
// Story 20.2 — Keywords Explorer Overview transformer (AC #2, #9, #14)
// ----------------------------------------------------------------------------

/**
 * Loosely-typed row shape for `/keywords-explorer/overview` (~21 documented
 * fields). The transformer is defensive — Ahrefs has expanded this surface
 * over time; rows may carry extra fields we don't recognize.
 */
interface KeywordsExplorerOverviewRowRaw {
  readonly keyword?: string | null;
  readonly clicks?: number | null;
  readonly cpc?: number | null; // cents
  readonly cps?: number | null;
  readonly difficulty?: number | null;
  readonly global_volume?: number | null;
  readonly intents?: Record<string, boolean> | null;
  readonly parent_topic?: string | null;
  readonly parent_volume?: number | null;
  readonly searches_pct_clicks_organic_and_paid?: number | null;
  readonly searches_pct_clicks_organic_only?: number | null;
  readonly searches_pct_clicks_paid_only?: number | null;
  readonly serp_features?: ReadonlyArray<string> | null;
  readonly serp_last_update?: string | null;
  readonly traffic_potential?: number | null;
  readonly volume?: number | null;
  readonly volume_desktop_pct?: number | null;
  readonly volume_mobile_pct?: number | null;
  // Story 35.8 — long-tail fields (default-select expansion).
  readonly volume_monthly?: number | null;
  readonly volume_monthly_history?: ReadonlyArray<{
    readonly date: string;
    readonly value: number;
  }> | null;
  readonly first_seen?: string | null;
}

/**
 * Transform a single `/keywords-explorer/overview` row. Sets `_reportType`
 * discriminator, converts `cpc` cents → dollars, populates the synthetic
 * `keywordDifficulty` + `costPerClick` + `clicksPerSearch` mirrors per AC #14.
 */
export function transformAhrefsKeywordsExplorerOverviewRow(
  raw: KeywordsExplorerOverviewRowRaw,
  country: string
): Record<string, unknown> {
  return {
    _reportType: "keywords-explorer-overview" satisfies AhrefsReportType,
    country,
    // Wire-format fields (grep-friendly for widget-config debugging).
    keyword: raw.keyword ?? null,
    clicks: raw.clicks ?? null,
    cpc: transformAhrefsCurrencyField(raw.cpc),
    cps: raw.cps ?? null,
    difficulty: raw.difficulty ?? null,
    global_volume: raw.global_volume ?? null,
    intents: raw.intents ?? null,
    parent_topic: raw.parent_topic ?? null,
    parent_volume: raw.parent_volume ?? null,
    searches_pct_clicks_organic_and_paid:
      raw.searches_pct_clicks_organic_and_paid ?? null,
    searches_pct_clicks_organic_only: raw.searches_pct_clicks_organic_only ?? null,
    searches_pct_clicks_paid_only: raw.searches_pct_clicks_paid_only ?? null,
    serp_features: raw.serp_features ?? null,
    serp_last_update: raw.serp_last_update ?? null,
    traffic_potential: raw.traffic_potential ?? null,
    volume: raw.volume ?? null,
    volume_desktop_pct: raw.volume_desktop_pct ?? null,
    volume_mobile_pct: raw.volume_mobile_pct ?? null,
    // Story 35.8 — Keywords Explorer long-tail fields (catalogue rows 79, 93,
    // 94 per ahrefs.md). `volume_monthly_history` ships as opaque
    // Array<{date, value}>; Story 20.3 line-chart widget renders via direct
    // wire-key access (no per-element transformer pair). Snake-case wire
    // fields paired with camelCase synthetic mirrors per Story 20.2
    // dual-naming rule (AC #14) — code-review pass 1 (M4) — matches the
    // existing `keywordDifficulty` / `costPerClick` / `clicksPerSearch`
    // convention below.
    first_seen: raw.first_seen ?? null,
    keywordsExplorerFirstSeen: raw.first_seen ?? null,
    volume_monthly: raw.volume_monthly ?? null,
    keywordsExplorerVolumeMonthly: raw.volume_monthly ?? null,
    volume_monthly_history: raw.volume_monthly_history ?? null,
    keywordsExplorerVolumeMonthlyHistory: raw.volume_monthly_history ?? null,
    // Synthetic camelCase mirrors (AC #14).
    keywordDifficulty: raw.difficulty ?? null,
    costPerClick: transformAhrefsCurrencyField(raw.cpc),
    clicksPerSearch: raw.cps ?? null,
  };
}

/**
 * Transform an array of `/keywords-explorer/overview` rows.
 *
 * `country` is captured from the request params (Ahrefs does not echo it in
 * each row but widgets need to label rows per-country in multi-country dashboards).
 */
export function transformAhrefsKeywordsExplorerOverviewResponse(
  raw: ReadonlyArray<KeywordsExplorerOverviewRowRaw>,
  country: string
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsKeywordsExplorerOverviewRow(row, country));
}

// ----------------------------------------------------------------------------
// Story 20.2 — Organic Keywords transformer (AC #3, #9, #14)
// ----------------------------------------------------------------------------

interface OrganicKeywordsRowRaw {
  readonly keyword?: string | null;
  readonly best_position?: number | null;
  readonly best_position_diff?: number | null;
  readonly best_position_kind?: string | null;
  readonly best_position_url?: string | null;
  readonly is_best_position_set_top_3?: boolean | null;
  readonly is_best_position_set_top_4_10?: boolean | null;
  readonly is_best_position_set_top_11_50?: boolean | null;
  readonly serp_features?: ReadonlyArray<string> | null;
  readonly serp_features_count?: number | null;
  readonly keyword_difficulty?: number | null;
  readonly keyword_country?: string | null;
  readonly volume?: number | null;
  readonly volume_desktop_pct?: number | null;
  readonly volume_mobile_pct?: number | null;
  readonly sum_traffic?: number | null;
  readonly sum_paid_traffic?: number | null;
  readonly cpc?: number | null; // cents
  readonly cpc_prev?: number | null; // cents
  readonly cpc_merged?: number | null; // cents
  readonly is_commercial?: boolean | null;
  readonly is_informational?: boolean | null;
  readonly is_branded?: boolean | null;
  readonly is_navigational?: boolean | null;
  readonly is_transactional?: boolean | null;
  readonly is_local?: boolean | null;
}

/**
 * Transform a single `/site-explorer/organic-keywords` row.
 *
 * Sets `_reportType: "organic-keywords"`. Converts `cpc` / `cpc_prev` /
 * `cpc_merged` cents → dollars. Populates synthetic `keywordDifficulty` +
 * `costPerClick` mirrors per AC #14. Carries `target` + `mode` + optional
 * `dateCompared` from the request params for widget-rendering context.
 */
export function transformAhrefsOrganicKeywordsRow(
  raw: OrganicKeywordsRowRaw,
  target: string,
  mode: string,
  dateCompared?: string
): Record<string, unknown> {
  return {
    _reportType: "organic-keywords" satisfies AhrefsReportType,
    target,
    mode,
    ...(dateCompared ? { dateCompared } : {}),
    // Wire-format fields.
    keyword: raw.keyword ?? null,
    best_position: raw.best_position ?? null,
    best_position_diff: raw.best_position_diff ?? null,
    best_position_kind: raw.best_position_kind ?? null,
    best_position_url: raw.best_position_url ?? null,
    is_best_position_set_top_3: raw.is_best_position_set_top_3 ?? null,
    is_best_position_set_top_4_10: raw.is_best_position_set_top_4_10 ?? null,
    is_best_position_set_top_11_50: raw.is_best_position_set_top_11_50 ?? null,
    serp_features: raw.serp_features ?? null,
    serp_features_count: raw.serp_features_count ?? null,
    keyword_difficulty: raw.keyword_difficulty ?? null,
    keyword_country: raw.keyword_country ?? null,
    volume: raw.volume ?? null,
    volume_desktop_pct: raw.volume_desktop_pct ?? null,
    volume_mobile_pct: raw.volume_mobile_pct ?? null,
    sum_traffic: raw.sum_traffic ?? null,
    sum_paid_traffic: raw.sum_paid_traffic ?? null,
    cpc: transformAhrefsCurrencyField(raw.cpc),
    cpc_prev: transformAhrefsCurrencyField(raw.cpc_prev),
    cpc_merged: transformAhrefsCurrencyField(raw.cpc_merged),
    is_commercial: raw.is_commercial ?? null,
    is_informational: raw.is_informational ?? null,
    is_branded: raw.is_branded ?? null,
    is_navigational: raw.is_navigational ?? null,
    is_transactional: raw.is_transactional ?? null,
    is_local: raw.is_local ?? null,
    // Synthetic camelCase mirrors.
    keywordDifficulty: raw.keyword_difficulty ?? null,
    costPerClick: transformAhrefsCurrencyField(raw.cpc),
    bestPositionDiff: raw.best_position_diff ?? null,
    bestPosition: raw.best_position ?? null,
  };
}

export function transformAhrefsOrganicKeywordsResponse(
  raw: ReadonlyArray<OrganicKeywordsRowRaw>,
  target: string,
  mode: string,
  dateCompared?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsOrganicKeywordsRow(row, target, mode, dateCompared)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2 — Rank Tracker Competitors Stats transformer (AC #4, #9)
// ----------------------------------------------------------------------------

interface RankTrackerCompetitorsStatsRowRaw {
  readonly competitor?: string | null;
  readonly average_position?: number | null;
  readonly share_of_voice?: number | null; // ratio 0-1, NOT cents
  readonly share_of_traffic_value?: number | null; // ratio 0-1, NOT cents
  readonly traffic?: number | null;
  readonly traffic_value?: number | null; // cents
  readonly ai_overview_count?: number | null;
  readonly pos_1_3?: number | null;
  readonly pos_4_10?: number | null;
  readonly pos_11_20?: number | null;
  readonly pos_21_50?: number | null;
  readonly pos_51_plus?: number | null;
  readonly pos_no_rank?: number | null;
  readonly discussions_count?: number | null;
  readonly featured_snippet_count?: number | null;
  readonly image_pack_count?: number | null;
  readonly knowledge_card_count?: number | null;
  readonly knowledge_panel_count?: number | null;
  readonly sitelinks_count?: number | null;
  readonly thumbnail_count?: number | null;
  readonly top_stories_count?: number | null;
  readonly video_preview_count?: number | null;
  readonly videos_count?: number | null;
  readonly x_count?: number | null;
}

/**
 * Transform a single `/rank-tracker/competitors-stats` row.
 *
 * **Currency note (AC #9):** `traffic_value` is in CENTS — convert via
 * `transformAhrefsCurrencyField`. `share_of_voice` and `share_of_traffic_value`
 * are RATIOS (0-1) per docs — do NOT divide by 100. Documented here so a
 * future agent doesn't "helpfully" add a divisor.
 */
export function transformAhrefsRankTrackerCompetitorsStatsRow(
  raw: RankTrackerCompetitorsStatsRowRaw,
  projectId: number,
  device: "desktop" | "mobile",
  date: string
): Record<string, unknown> {
  return {
    _reportType: "rank-tracker-competitors-stats" satisfies AhrefsReportType,
    projectId,
    device,
    date,
    competitor: raw.competitor ?? null,
    average_position: raw.average_position ?? null,
    // Ratios — NEVER divide by 100.
    share_of_voice: raw.share_of_voice ?? null,
    share_of_traffic_value: raw.share_of_traffic_value ?? null,
    traffic: raw.traffic ?? null,
    // Currency cents — divide by 100.
    traffic_value: transformAhrefsCurrencyField(raw.traffic_value),
    ai_overview_count: raw.ai_overview_count ?? null,
    pos_1_3: raw.pos_1_3 ?? null,
    pos_4_10: raw.pos_4_10 ?? null,
    pos_11_20: raw.pos_11_20 ?? null,
    pos_21_50: raw.pos_21_50 ?? null,
    pos_51_plus: raw.pos_51_plus ?? null,
    pos_no_rank: raw.pos_no_rank ?? null,
    discussions_count: raw.discussions_count ?? null,
    featured_snippet_count: raw.featured_snippet_count ?? null,
    image_pack_count: raw.image_pack_count ?? null,
    knowledge_card_count: raw.knowledge_card_count ?? null,
    knowledge_panel_count: raw.knowledge_panel_count ?? null,
    sitelinks_count: raw.sitelinks_count ?? null,
    thumbnail_count: raw.thumbnail_count ?? null,
    top_stories_count: raw.top_stories_count ?? null,
    video_preview_count: raw.video_preview_count ?? null,
    videos_count: raw.videos_count ?? null,
    x_count: raw.x_count ?? null,
    // Synthetic camelCase mirrors.
    shareOfVoice: raw.share_of_voice ?? null,
    shareOfTrafficValue: raw.share_of_traffic_value ?? null,
    trafficValue: transformAhrefsCurrencyField(raw.traffic_value),
  };
}

export function transformAhrefsRankTrackerCompetitorsStatsResponse(
  raw: ReadonlyArray<RankTrackerCompetitorsStatsRowRaw>,
  projectId: number,
  device: "desktop" | "mobile",
  date: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsRankTrackerCompetitorsStatsRow(row, projectId, device, date)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2 — Keywords History transformer (AC #5)
// ----------------------------------------------------------------------------

interface KeywordsHistoryRowRaw {
  readonly date?: string | null;
  readonly top3?: number | null;
  readonly top4_10?: number | null;
  readonly top11_20?: number | null;
  readonly top21_50?: number | null;
  readonly top51_plus?: number | null;
  /** Legacy field — present on older responses; kept for backward compat. */
  readonly top11_plus?: number | null;
}

/**
 * Transform a single `/site-explorer/keywords-history` row. NO currency
 * conversion — every field on this endpoint is a count.
 */
export function transformAhrefsKeywordsHistoryRow(
  raw: KeywordsHistoryRowRaw,
  target: string,
  mode: string
): Record<string, unknown> {
  return {
    _reportType: "keywords-history" satisfies AhrefsReportType,
    target,
    mode,
    date: raw.date ?? null,
    top3: raw.top3 ?? null,
    top4_10: raw.top4_10 ?? null,
    top11_20: raw.top11_20 ?? null,
    top21_50: raw.top21_50 ?? null,
    top51_plus: raw.top51_plus ?? null,
    // Legacy field — preserved for widgets that still bind to it. New
    // widgets MUST use the granular buckets above per the 2026 changelog.
    top11_plus: raw.top11_plus ?? null,
  };
}

export function transformAhrefsKeywordsHistoryResponse(
  raw: ReadonlyArray<KeywordsHistoryRowRaw>,
  target: string,
  mode: string
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsKeywordsHistoryRow(row, target, mode));
}

// ----------------------------------------------------------------------------
// Story 20.2a — Metrics History transformer (AC #7)
// ----------------------------------------------------------------------------

interface MetricsHistoryRowRaw {
  readonly date?: string | null;
  readonly org_cost?: number | null; // cents
  readonly org_traffic?: number | null;
  readonly paid_cost?: number | null; // cents
  readonly paid_traffic?: number | null;
}

/**
 * Transform a single `/site-explorer/metrics-history` row.
 *
 * Sets `_reportType: "metrics-history"`. Converts `org_cost` + `paid_cost`
 * cents → dollars (BOTH currency fields documented on this endpoint per
 * docs.ahrefs.com on 2026-05-03). Preserves the wire-format snake_case keys
 * AND emits camelCase mirrors (`organicTraffic`, `paidTraffic`,
 * `organicTrafficValue`, `paidTrafficCost`) matching the Story 20.1 `/metrics`
 * transformer so widgets can dispatch on a single key regardless of source.
 */
export function transformAhrefsMetricsHistoryRow(
  raw: MetricsHistoryRowRaw,
  target: string,
  mode: string
): Record<string, unknown> {
  return {
    _reportType: "metrics-history" satisfies AhrefsReportType,
    target,
    mode,
    // Wire-format fields (grep-friendly for debugging against Ahrefs docs).
    date: raw.date ?? null,
    org_cost: transformAhrefsCurrencyField(raw.org_cost),
    org_traffic: raw.org_traffic ?? null,
    paid_cost: transformAhrefsCurrencyField(raw.paid_cost),
    paid_traffic: raw.paid_traffic ?? null,
    // Synthetic camelCase mirrors — match the existing `/metrics` transformer.
    organicTraffic: raw.org_traffic ?? null,
    organicTrafficValue: transformAhrefsCurrencyField(raw.org_cost),
    paidTraffic: raw.paid_traffic ?? null,
    paidTrafficCost: transformAhrefsCurrencyField(raw.paid_cost),
  };
}

export function transformAhrefsMetricsHistoryResponse(
  raw: ReadonlyArray<MetricsHistoryRowRaw>,
  target: string,
  mode: string
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsMetricsHistoryRow(row, target, mode));
}

// ----------------------------------------------------------------------------
// Story 20.2a — Metrics by Country transformer (AC #7)
// ----------------------------------------------------------------------------

interface MetricsByCountryRowRaw {
  readonly country?: string | null;
  readonly org_cost?: number | null; // cents
  readonly org_keywords?: number | null;
  readonly org_keywords_1_3?: number | null;
  readonly org_traffic?: number | null;
  readonly paid_cost?: number | null; // cents
  readonly paid_keywords?: number | null;
  readonly paid_pages?: number | null;
  readonly paid_traffic?: number | null;
}

/**
 * Transform a single `/site-explorer/metrics-by-country` row.
 *
 * Sets `_reportType: "metrics-by-country"`. Converts `org_cost` + `paid_cost`
 * cents → dollars. Preserves the wire-format snake_case keys AND emits
 * camelCase mirrors matching the Story 20.1 `/metrics` transformer so widgets
 * can dispatch on a single key regardless of source endpoint.
 */
export function transformAhrefsMetricsByCountryRow(
  raw: MetricsByCountryRowRaw,
  target: string,
  mode: string
): Record<string, unknown> {
  return {
    _reportType: "metrics-by-country" satisfies AhrefsReportType,
    target,
    mode,
    // Wire-format fields.
    country: raw.country ?? null,
    org_cost: transformAhrefsCurrencyField(raw.org_cost),
    org_keywords: raw.org_keywords ?? null,
    org_keywords_1_3: raw.org_keywords_1_3 ?? null,
    org_traffic: raw.org_traffic ?? null,
    paid_cost: transformAhrefsCurrencyField(raw.paid_cost),
    paid_keywords: raw.paid_keywords ?? null,
    paid_pages: raw.paid_pages ?? null,
    paid_traffic: raw.paid_traffic ?? null,
    // Synthetic camelCase mirrors.
    organicKeywords: raw.org_keywords ?? null,
    organicKeywordsTop3: raw.org_keywords_1_3 ?? null,
    organicTraffic: raw.org_traffic ?? null,
    organicTrafficValue: transformAhrefsCurrencyField(raw.org_cost),
    paidKeywords: raw.paid_keywords ?? null,
    paidPages: raw.paid_pages ?? null,
    paidTraffic: raw.paid_traffic ?? null,
    paidTrafficCost: transformAhrefsCurrencyField(raw.paid_cost),
  };
}

export function transformAhrefsMetricsByCountryResponse(
  raw: ReadonlyArray<MetricsByCountryRowRaw>,
  target: string,
  mode: string
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsMetricsByCountryRow(row, target, mode));
}

// ----------------------------------------------------------------------------
// Story 20.2c — Rank Tracker per-keyword `/overview` transformer (AC #4, #11)
// ----------------------------------------------------------------------------

/**
 * Loosely-typed row shape for `/rank-tracker/overview`. Every field is optional
 * — Ahrefs only returns the columns named in the request `select`.
 *
 * `cost_per_click` is the ONLY currency field returned by this endpoint.
 * `traffic_value`, `share_of_voice`, and `share_of_traffic_value` do NOT
 * exist on `/overview` (they live on `/competitors-stats`); the transformer
 * does NOT read them.
 */
interface RankTrackerOverviewRowRaw {
  // Position & ranking
  readonly best_position_has_thumbnail?: boolean | null;
  readonly best_position_has_thumbnail_previous?: boolean | null;
  readonly best_position_has_video_preview?: boolean | null;
  readonly best_position_has_video_preview_previous?: boolean | null;
  readonly best_position_kind?: string | null;
  readonly best_position_kind_previous?: string | null;
  readonly position?: number | null;
  readonly position_prev?: number | null;
  readonly position_diff?: number | null;
  readonly is_main_position?: boolean | null;
  readonly is_main_position_prev?: boolean | null;
  readonly target_positions_count?: number | null;
  // Keyword & search data
  readonly keyword?: string | null;
  readonly keyword_prev?: string | null;
  readonly keyword_difficulty?: number | null;
  readonly keyword_has_data?: boolean | null;
  readonly keyword_is_frozen?: boolean | null;
  readonly keyword_words?: number | null;
  readonly keyword_words_prev?: number | null;
  readonly volume?: number | null;
  readonly volume_desktop_pct?: number | null;
  readonly volume_mobile_pct?: number | null;
  readonly clicks?: number | null;
  readonly clicks_per_search?: number | null;
  readonly cost_per_click?: number | null; // cents
  readonly traffic?: number | null;
  readonly traffic_prev?: number | null;
  readonly traffic_diff?: number | null;
  readonly parent_topic?: string | null;
  readonly serp_features?: ReadonlyArray<string> | null;
  readonly serp_features_prev?: ReadonlyArray<string> | null;
  // Location & language
  readonly country?: string | null;
  readonly country_prev?: string | null;
  readonly location?: string | null;
  readonly location_prev?: string | null;
  readonly language?: string | null;
  readonly language_prev?: string | null;
  // Search type distribution
  readonly search_type_web?: number | null;
  readonly search_type_image?: number | null;
  readonly search_type_news?: number | null;
  readonly search_type_video?: number | null;
  // User intent flags
  readonly is_branded?: boolean | null;
  readonly is_commercial?: boolean | null;
  readonly is_informational?: boolean | null;
  readonly is_local?: boolean | null;
  readonly is_navigational?: boolean | null;
  readonly is_transactional?: boolean | null;
  // Metadata
  readonly created_at?: string | null;
  readonly serp_updated?: string | null;
  readonly serp_updated_prev?: string | null;
  readonly tags?: ReadonlyArray<string> | null;
  readonly tags_prev?: ReadonlyArray<string> | null;
  readonly url?: string | null;
  readonly url_prev?: string | null;
}

/**
 * Story 20.2c AC #11 — synthetic `positionMomentum`.
 *
 * Signed integer where positive = "moved up" (lower numerical position is
 * better in SERP terms), negative = "moved down". Returns `null` when:
 *  - either input is null/undefined
 *  - either input is non-finite (NaN, ±Infinity)
 *  - keyword has no fresh data (`keyword_has_data === false`) — stale data
 *    must not produce a misleading "movement" signal
 *
 * Exported for test coverage; consumers in this file use it directly.
 */
export function computePositionMomentum(
  position: number | null | undefined,
  positionPrev: number | null | undefined,
  keywordHasData: boolean | null | undefined
): number | null {
  if (keywordHasData === false) return null;
  if (position === null || position === undefined) return null;
  if (positionPrev === null || positionPrev === undefined) return null;
  if (!Number.isFinite(position) || !Number.isFinite(positionPrev)) return null;
  return positionPrev - position;
}

/**
 * Story 20.2c AC #11 — synthetic `appearedToday`.
 *
 * Boolean — `true` when the keyword newly appeared on `date` after being
 * absent on `date_compared` (or when no compared date was supplied AND the
 * keyword has fresh data). `false` when the keyword is frozen or the wire
 * `keyword_has_data` is not literally `true`.
 *
 * Both `keyword_is_frozen` AND `keyword_has_data` are checked: a frozen
 * keyword may still have lingering `keyword_has_data=true` if the freeze
 * happened mid-cycle, and the spec says "false when keyword is frozen"
 * regardless of the data-freshness flag.
 *
 * Conservative semantics: returns `false` (not `null`) on missing data so
 * widget badges have a deterministic value.
 */
export function computeAppearedToday(
  positionPrev: number | null | undefined,
  keywordHasData: boolean | null | undefined,
  keywordIsFrozen?: boolean | null
): boolean {
  if (keywordIsFrozen === true) return false;
  if (keywordHasData !== true) return false;
  return positionPrev === null || positionPrev === undefined;
}

/**
 * Transform a single `/rank-tracker/overview` row.
 *
 * Sets `_reportType: "rank-tracker-overview"`. Preserves every wire-format
 * snake_case field (per the dual-naming rule from Story 20.2 AC #14) and
 * emits 9 synthetic camelCase mirrors per AC #4 plus 2 NaN-safe synthetic
 * derived metrics per AC #11 (`positionMomentum`, `appearedToday`).
 *
 * Currency: `cost_per_click` is the ONLY currency field on this endpoint
 * (cents → dollars via `transformAhrefsCurrencyField`). The 3 currency-like
 * fields from `/competitors-stats` (`traffic_value`, `share_of_voice`,
 * `share_of_traffic_value`) do NOT exist on `/overview` — the transformer
 * does not read them.
 */
export function transformAhrefsRankTrackerOverviewRow(
  raw: RankTrackerOverviewRowRaw,
  projectId: number,
  device: "desktop" | "mobile",
  date: string,
  dateCompared?: string
): Record<string, unknown> {
  const positionMomentum = computePositionMomentum(
    raw.position ?? null,
    raw.position_prev ?? null,
    raw.keyword_has_data
  );
  const appearedToday = computeAppearedToday(
    raw.position_prev ?? null,
    raw.keyword_has_data,
    raw.keyword_is_frozen
  );

  return {
    _reportType: "rank-tracker-overview" satisfies AhrefsReportType,
    projectId,
    device,
    date,
    ...(dateCompared ? { dateCompared } : {}),
    // Wire-format fields (preserved per dual-naming rule — grep-friendly
    // for widget-config debugging against Ahrefs docs).
    best_position_has_thumbnail: raw.best_position_has_thumbnail ?? null,
    best_position_has_thumbnail_previous: raw.best_position_has_thumbnail_previous ?? null,
    best_position_has_video_preview: raw.best_position_has_video_preview ?? null,
    best_position_has_video_preview_previous:
      raw.best_position_has_video_preview_previous ?? null,
    best_position_kind: raw.best_position_kind ?? null,
    best_position_kind_previous: raw.best_position_kind_previous ?? null,
    position: raw.position ?? null,
    position_prev: raw.position_prev ?? null,
    position_diff: raw.position_diff ?? null,
    is_main_position: raw.is_main_position ?? null,
    is_main_position_prev: raw.is_main_position_prev ?? null,
    target_positions_count: raw.target_positions_count ?? null,
    keyword: raw.keyword ?? null,
    keyword_prev: raw.keyword_prev ?? null,
    keyword_difficulty: raw.keyword_difficulty ?? null,
    keyword_has_data: raw.keyword_has_data ?? null,
    keyword_is_frozen: raw.keyword_is_frozen ?? null,
    keyword_words: raw.keyword_words ?? null,
    keyword_words_prev: raw.keyword_words_prev ?? null,
    volume: raw.volume ?? null,
    volume_desktop_pct: raw.volume_desktop_pct ?? null,
    volume_mobile_pct: raw.volume_mobile_pct ?? null,
    clicks: raw.clicks ?? null,
    clicks_per_search: raw.clicks_per_search ?? null,
    cost_per_click: transformAhrefsCurrencyField(raw.cost_per_click),
    traffic: raw.traffic ?? null,
    traffic_prev: raw.traffic_prev ?? null,
    traffic_diff: raw.traffic_diff ?? null,
    parent_topic: raw.parent_topic ?? null,
    serp_features: raw.serp_features ?? null,
    serp_features_prev: raw.serp_features_prev ?? null,
    country: raw.country ?? null,
    country_prev: raw.country_prev ?? null,
    location: raw.location ?? null,
    location_prev: raw.location_prev ?? null,
    language: raw.language ?? null,
    language_prev: raw.language_prev ?? null,
    search_type_web: raw.search_type_web ?? null,
    search_type_image: raw.search_type_image ?? null,
    search_type_news: raw.search_type_news ?? null,
    search_type_video: raw.search_type_video ?? null,
    is_branded: raw.is_branded ?? null,
    is_commercial: raw.is_commercial ?? null,
    is_informational: raw.is_informational ?? null,
    is_local: raw.is_local ?? null,
    is_navigational: raw.is_navigational ?? null,
    is_transactional: raw.is_transactional ?? null,
    created_at: raw.created_at ?? null,
    serp_updated: raw.serp_updated ?? null,
    serp_updated_prev: raw.serp_updated_prev ?? null,
    tags: raw.tags ?? null,
    tags_prev: raw.tags_prev ?? null,
    url: raw.url ?? null,
    url_prev: raw.url_prev ?? null,
    // Synthetic camelCase mirrors (AC #4) — align with the existing dual-
    // naming registry so widgets can dispatch on a single key regardless of
    // which endpoint sourced the row.
    keywordDifficulty: raw.keyword_difficulty ?? null,
    costPerClick: transformAhrefsCurrencyField(raw.cost_per_click),
    clicksPerSearch: raw.clicks_per_search ?? null,
    bestPositionKind: raw.best_position_kind ?? null,
    positionDiff: raw.position_diff ?? null,
    positionPrev: raw.position_prev ?? null,
    trafficDiff: raw.traffic_diff ?? null,
    trafficPrev: raw.traffic_prev ?? null,
    serpFeaturesPrev: raw.serp_features_prev ?? null,
    // Synthetic NaN-safe derived metrics (AC #11) — registered in
    // AHREFS_SYNTHETIC_METRIC_KEYS so the apiField allowlist tests exempt them.
    positionMomentum,
    appearedToday,
  };
}

export function transformAhrefsRankTrackerOverviewResponse(
  raw: ReadonlyArray<RankTrackerOverviewRowRaw>,
  projectId: number,
  device: "desktop" | "mobile",
  date: string,
  dateCompared?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsRankTrackerOverviewRow(row, projectId, device, date, dateCompared)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2d — Site Audit /projects transformer (AC #1)
// ----------------------------------------------------------------------------

/**
 * Loosely-typed row shape for `/site-audit/projects`. Every field is optional
 * because Ahrefs only returns the columns named in the request `select`.
 */
interface SiteAuditProjectRowRaw {
  readonly project_id?: string | null;
  readonly project_name?: string | null;
  readonly target_protocol?: string | null;
  readonly target_url?: string | null;
  readonly target_mode?: string | null;
  readonly date?: string | null;
  readonly status?: string | null;
  readonly health_score?: number | null;
  readonly urls_with_errors?: number | null;
  readonly urls_with_warnings?: number | null;
  readonly urls_with_notices?: number | null;
  readonly total?: number | null;
}

/**
 * Transform a single `/site-audit/projects` row.
 *
 * All 12 wire-format fields are already readable as-is (project_id, project_name,
 * etc.); no camelCase mirrors are emitted because the field names are simple
 * single-token-per-segment snake_case. `health_score` is preserved as-is —
 * note the wire format returns `null` for crawls in-progress, which the
 * transformer faithfully passes through so widgets render "no data" rather
 * than misleading 0-of-100 health.
 */
export function transformAhrefsSiteAuditProjectRow(
  raw: SiteAuditProjectRowRaw
): Record<string, unknown> {
  return {
    _reportType: "site-audit-projects" satisfies AhrefsReportType,
    project_id: raw.project_id ?? null,
    project_name: raw.project_name ?? null,
    target_protocol: raw.target_protocol ?? null,
    target_url: raw.target_url ?? null,
    target_mode: raw.target_mode ?? null,
    date: raw.date ?? null,
    status: raw.status ?? null,
    health_score: raw.health_score ?? null,
    urls_with_errors: raw.urls_with_errors ?? null,
    urls_with_warnings: raw.urls_with_warnings ?? null,
    urls_with_notices: raw.urls_with_notices ?? null,
    total: raw.total ?? null,
  };
}

export function transformAhrefsSiteAuditProjectsResponse(
  raw: ReadonlyArray<SiteAuditProjectRowRaw>
): Array<Record<string, unknown>> {
  return raw.map((row) => transformAhrefsSiteAuditProjectRow(row));
}

// ----------------------------------------------------------------------------
// Story 20.2d — Site Audit /issues transformer (AC #1)
// ----------------------------------------------------------------------------

/**
 * Loosely-typed row shape for `/site-audit/issues`.
 *
 * `importance` and `category` are wire-format string enums — preserved verbatim:
 *  - `importance` ∈ `Error | Warning | Notice` (capitalized).
 *  - `category` ∈ 15 documented values: `Internal pages`, `Indexability`,
 *    `Links`, `Redirects`, `Content`, `Social tags`, `Duplicates`,
 *    `Localization`, `Usability and performance`, `Images`, `JavaScript`,
 *    `CSS`, `Sitemaps`, `External pages`, `Other`.
 *
 * The 5 comparison fields (`change`/`added`/`new`/`removed`/`missing`) are
 * `null` when `date_compared` is omitted; the transformer preserves null
 * (does NOT default to 0) so widgets can render "no comparison data" rather
 * than a misleading zero delta.
 */
interface SiteAuditIssueRowRaw {
  readonly issue_id?: string | null;
  readonly name?: string | null;
  readonly importance?: string | null;
  readonly category?: string | null;
  readonly is_indexable?: boolean | null;
  readonly crawled?: number | null;
  readonly change?: number | null;
  readonly added?: number | null;
  readonly new?: number | null;
  readonly removed?: number | null;
  readonly missing?: number | null;
}

/**
 * Transform a single `/site-audit/issues` row.
 *
 * Per the Story 20.2 dual-naming rule (AC #14), preserves wire-format
 * snake_case AND emits 2 camelCase mirrors for the multi-word fields whose
 * camelCase form is the canonical widget key:
 *  - `issueId` (vs wire `issue_id`)
 *  - `isIndexable` (vs wire `is_indexable`)
 *
 * The simple single-token fields (`name`, `category`, `crawled`, `change`,
 * `added`, `new`, `removed`, `missing`) ship wire-format-only. `projectId`,
 * `date`, `dateCompared` are propagated as row-level metadata so widgets that
 * render multi-project dashboards can group rows correctly.
 */
export function transformAhrefsSiteAuditIssueRow(
  raw: SiteAuditIssueRowRaw,
  projectId: number,
  date?: string,
  dateCompared?: string
): Record<string, unknown> {
  return {
    _reportType: "site-audit-issues" satisfies AhrefsReportType,
    projectId,
    ...(date ? { date } : {}),
    ...(dateCompared ? { dateCompared } : {}),
    // Wire-format fields.
    issue_id: raw.issue_id ?? null,
    name: raw.name ?? null,
    importance: raw.importance ?? null,
    category: raw.category ?? null,
    is_indexable: raw.is_indexable ?? null,
    crawled: raw.crawled ?? null,
    // 5 comparison fields — null preserved (NOT defaulted to 0).
    change: raw.change ?? null,
    added: raw.added ?? null,
    new: raw.new ?? null,
    removed: raw.removed ?? null,
    missing: raw.missing ?? null,
    // CamelCase mirrors per dual-naming rule.
    issueId: raw.issue_id ?? null,
    isIndexable: raw.is_indexable ?? null,
  };
}

export function transformAhrefsSiteAuditIssuesResponse(
  raw: ReadonlyArray<SiteAuditIssueRowRaw>,
  projectId: number,
  date?: string,
  dateCompared?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsSiteAuditIssueRow(row, projectId, date, dateCompared)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2d — Site Audit /page-explorer transformer (AC #1)
// ----------------------------------------------------------------------------

/**
 * Transform a single `/site-audit/page-explorer` row.
 *
 * The 608-field SDK response surface is widget-config-driven; the transformer
 * passes the row through unchanged with the discriminator + project metadata
 * stamped on. Story 20.3 will surface field-level metric definitions for the
 * 12 priority page-explorer fields when the widget config UI lands.
 */
export function transformAhrefsSiteAuditPageExplorerRow(
  raw: Record<string, unknown>,
  projectId: number,
  date?: string,
  dateCompared?: string
): Record<string, unknown> {
  // Spread `...raw` FIRST then the metadata fields LAST so adapter-stamped
  // discriminator + projectId / date / dateCompared cannot be silently
  // overwritten by an unexpected key in the raw row (e.g. if Ahrefs ever
  // adds `_reportType` or `projectId` columns to /page-explorer). Code Review
  // Pass 1 hardening — protects against widget-render dispatch breakage.
  return {
    ...raw,
    _reportType: "site-audit-page-explorer" satisfies AhrefsReportType,
    projectId,
    ...(date ? { date } : {}),
    ...(dateCompared ? { dateCompared } : {}),
  };
}

export function transformAhrefsSiteAuditPageExplorerResponse(
  raw: ReadonlyArray<Record<string, unknown>>,
  projectId: number,
  date?: string,
  dateCompared?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsSiteAuditPageExplorerRow(row, projectId, date, dateCompared)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2d — Site Audit /page-content transformer (AC #1)
// ----------------------------------------------------------------------------

interface SiteAuditPageContentRaw {
  readonly crawl_datetime?: string | null;
  readonly page_text?: string | null;
  readonly raw_html?: string | null;
  readonly rendered_html?: string | null;
}

/**
 * Transform a `/site-audit/page-content` response.
 *
 * The endpoint returns a single content object (not an array). The transformer
 * stamps the discriminator + projectId/targetUrl metadata and passes the 4
 * content fields through verbatim. NOT a metric source — content extraction
 * surfaces text/HTML to consumers that need it; no `MetricDefinition` rows
 * exist for this report type.
 */
export function transformAhrefsSiteAuditPageContentResponse(
  raw: SiteAuditPageContentRaw,
  projectId: number,
  targetUrl: string
): Record<string, unknown> {
  return {
    _reportType: "site-audit-page-content" satisfies AhrefsReportType,
    projectId,
    targetUrl,
    crawl_datetime: raw.crawl_datetime ?? null,
    page_text: raw.page_text ?? null,
    raw_html: raw.raw_html ?? null,
    rendered_html: raw.rendered_html ?? null,
  };
}

// ============================================================================
// Story 20.2e — Brand Radar transformers (8 GET endpoints; 9 discriminators)
// ============================================================================
//
// Every transformer stamps `_reportType` + per-row metadata (brand /
// dataSource / dateRange / dateFrom / dateTo as appropriate) so widgets can
// dispatch on a single discriminator regardless of source.
//
// The 2 cited-* transformers strip the deprecated `mentions` field
// (deprecated 2026-02-10) and `volume` field (deprecated 2026-03-24) at ingest
// — emitting `null` regardless of wire payload. This protects widgets from
// rendering stale/obsolete data without forcing an immediate widget-config
// migration. Per AC #5: the `mentions` field on `/mentions-history` +
// `/mentions-overview` is the LEGITIMATE count metric (NOT deprecated).
//
// The /sov-history transformer preserves the `share_of_voice` nested array
// shape verbatim — agencies expect the per-date list of {brand, value} pairs
// so the widget render layer can plot a stacked time-series with brand-as-color.
// Flattening would force widgets to re-aggregate, which is wasteful and lossy.

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/ai-responses transformer
// ----------------------------------------------------------------------------

interface BrandRadarAiResponsesRowRaw {
  readonly country?: string | null;
  readonly question?: string | null;
  readonly response?: string | null;
  readonly links?: ReadonlyArray<string> | null;
  readonly search_queries?: ReadonlyArray<string> | null;
  readonly tags?: ReadonlyArray<string> | null;
  readonly volume?: number | null;
  // Note: `data_source` is NOT a per-row response field on /ai-responses per
  // the catalogue (rows 183-189 list 7 fields, none of them `data_source`).
  // The request-level data-source label is propagated via the `dataSource`
  // metadata key set by the transformer — we do NOT echo any per-row
  // `data_source` field, even if Ahrefs starts surfacing one in a future
  // schema (the metric-allowlist arm would need to be updated first).
}

/**
 * Transform a single `/brand-radar/ai-responses` row. Sets `_reportType`
 * discriminator and preserves wire-format snake_case fields verbatim — the
 * AHREFS_METRICS catalogue binds `apiField: "country"` to the per-row
 * response country (NOT the request-level country filter), so the wire key
 * MUST be preserved as `country` here. `data_source` (when echoed by Ahrefs
 * per row) is preserved so multi-AI-client widgets can group rows correctly.
 *
 * Per-row metadata: the request-level `dataSource` (joined wire literals) +
 * optional `dateRange` are propagated for widget context. The
 * `requestCountry` argument (when supplied) is propagated under a distinct
 * key so it does not collide with the wire-format `country` field — Code
 * Review Pass 1 hardening (C1).
 */
export function transformAhrefsBrandRadarAiResponsesRow(
  raw: BrandRadarAiResponsesRowRaw,
  dataSource: string,
  requestCountry?: string,
  date?: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-ai-responses" satisfies AhrefsReportType,
    dataSource,
    ...(requestCountry ? { requestCountry } : {}),
    ...(date ? { date } : {}),
    // Wire-format fields (preserved per dual-naming rule). `country` here is
    // the PER-ROW response country, NOT the request filter — `apiField:
    // "country"` on the catalogue's `brandRadarCountry` row binds to this key.
    // Code Review Pass 2 (M6): does NOT echo any per-row `data_source` —
    // the metric-allowlist arm contains exactly the 7 cataloged fields (rows
    // 183-189). The request-level data-source label flows via `dataSource`.
    country: raw.country ?? null,
    question: raw.question ?? null,
    response: raw.response ?? null,
    links: raw.links ?? null,
    search_queries: raw.search_queries ?? null,
    tags: raw.tags ?? null,
    volume: raw.volume ?? null,
  };
}

export function transformAhrefsBrandRadarAiResponsesResponse(
  raw: ReadonlyArray<BrandRadarAiResponsesRowRaw>,
  dataSource: string,
  requestCountry?: string,
  date?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarAiResponsesRow(row, dataSource, requestCountry, date)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/impressions-history transformer
// ----------------------------------------------------------------------------

interface BrandRadarImpressionsHistoryRowRaw {
  readonly date?: string | null;
  readonly impressions?: number | null;
}

/**
 * Transform a single `/brand-radar/impressions-history` row. Fixed 2-field
 * shape (`date`, `impressions`); the request-level `dataSource` and
 * `dateFrom`/`dateTo` are propagated as row-level metadata for widget
 * grouping.
 */
export function transformAhrefsBrandRadarImpressionsHistoryRow(
  raw: BrandRadarImpressionsHistoryRowRaw,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-impressions-history" satisfies AhrefsReportType,
    dataSource,
    dateFrom,
    ...(dateTo ? { dateTo } : {}),
    date: raw.date ?? null,
    impressions: raw.impressions ?? null,
  };
}

export function transformAhrefsBrandRadarImpressionsHistoryResponse(
  raw: ReadonlyArray<BrandRadarImpressionsHistoryRowRaw>,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarImpressionsHistoryRow(row, dataSource, dateFrom, dateTo)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/mentions-history transformer
// ----------------------------------------------------------------------------
//
// Note: `mentions` here is the LEGITIMATE count metric on /mentions-history
// (NOT the deprecated cited-* field). The metric-allowlist grep guardrail is
// narrowed to forbid `mentions` ONLY in cited-* contexts (per AC #5).

interface BrandRadarMentionsHistoryRowRaw {
  readonly date?: string | null;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- legitimate count metric on /mentions-history per AC #5; deprecation invariant only applies to /cited-* endpoints.
  readonly mentions?: number | null;
}

/**
 * Transform a single `/brand-radar/mentions-history` row. The `mentions`
 * field on this endpoint is the legitimate count metric per AC #5 — distinct
 * from the deprecated `mentions` field on `/cited-domains` and
 * `/cited-pages` (deprecated 2026-02-10). The metric-allowlist guardrail is
 * context-aware so this usage is legitimate.
 */
export function transformAhrefsBrandRadarMentionsHistoryRow(
  raw: BrandRadarMentionsHistoryRowRaw,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-mentions-history" satisfies AhrefsReportType,
    dataSource,
    dateFrom,
    ...(dateTo ? { dateTo } : {}),
    date: raw.date ?? null,
    // Legitimate count metric per AC #5 — NOT the deprecated cited-* field.
    mentions: raw.mentions ?? null,
  };
}

export function transformAhrefsBrandRadarMentionsHistoryResponse(
  raw: ReadonlyArray<BrandRadarMentionsHistoryRowRaw>,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarMentionsHistoryRow(row, dataSource, dateFrom, dateTo)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/impressions-overview + /brand-radar/mentions-overview
// ----------------------------------------------------------------------------
//
// Both endpoints share an identical 6-field row shape per AC #1. The shared
// transformer takes a discriminator argument so both report types route through
// one implementation while preserving distinct `_reportType` values.

interface BrandRadarOverviewRowRaw {
  readonly brand?: string | null;
  readonly only_target_brand?: number | null;
  readonly only_competitors_brands?: number | null;
  readonly target_and_competitors_brands?: number | null;
  readonly no_tracked_brands?: number | null;
  readonly total?: number | null;
}

function transformBrandRadarOverviewRowShared(
  raw: BrandRadarOverviewRowRaw,
  reportType:
    | "brand-radar-impressions-overview"
    | "brand-radar-mentions-overview",
  dataSource: string
): Record<string, unknown> {
  return {
    _reportType: reportType satisfies AhrefsReportType,
    dataSource,
    brand: raw.brand ?? null,
    only_target_brand: raw.only_target_brand ?? null,
    only_competitors_brands: raw.only_competitors_brands ?? null,
    target_and_competitors_brands: raw.target_and_competitors_brands ?? null,
    no_tracked_brands: raw.no_tracked_brands ?? null,
    total: raw.total ?? null,
  };
}

/**
 * Transform a single `/brand-radar/impressions-overview` row. Preserves the
 * 5-bucket integer shape (`only_target_brand` / `only_competitors_brands` /
 * `target_and_competitors_brands` / `no_tracked_brands` / `total`) verbatim
 * per AC #1 catalogue rows 194-199. R0 had this wrong — preserved by R1.
 */
export function transformAhrefsBrandRadarImpressionsOverviewRow(
  raw: BrandRadarOverviewRowRaw,
  dataSource: string
): Record<string, unknown> {
  return transformBrandRadarOverviewRowShared(
    raw,
    "brand-radar-impressions-overview",
    dataSource
  );
}

export function transformAhrefsBrandRadarImpressionsOverviewResponse(
  raw: ReadonlyArray<BrandRadarOverviewRowRaw>,
  dataSource: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarImpressionsOverviewRow(row, dataSource)
  );
}

/**
 * Transform a single `/brand-radar/mentions-overview` row. Same 5-bucket
 * integer shape as impressions-overview but counts brand mentions per AC #1
 * catalogue rows 200-205.
 */
export function transformAhrefsBrandRadarMentionsOverviewRow(
  raw: BrandRadarOverviewRowRaw,
  dataSource: string
): Record<string, unknown> {
  return transformBrandRadarOverviewRowShared(
    raw,
    "brand-radar-mentions-overview",
    dataSource
  );
}

export function transformAhrefsBrandRadarMentionsOverviewResponse(
  raw: ReadonlyArray<BrandRadarOverviewRowRaw>,
  dataSource: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarMentionsOverviewRow(row, dataSource)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/sov-overview transformer
// ----------------------------------------------------------------------------

interface BrandRadarSovOverviewRowRaw {
  readonly brand?: string | null;
  /** Float 0-1 per docs verification 2026-05-05 (NOT a percentage 0-100). */
  readonly share_of_voice?: number | null;
}

/**
 * Transform a single `/brand-radar/sov-overview` row.
 *
 * `share_of_voice` is a float 0-1 per docs (NOT cents, NOT percentage points).
 * Do NOT divide by 100 — the value is already in the canonical 0-1 range.
 * Widgets render this as percentage by multiplying by 100 at the render layer.
 *
 * Headline metric for the Story 20.3 widget UI (the canonical AI Share-of-Voice
 * gauge: "Your brand has 12% Share of Voice across ChatGPT/Gemini/...").
 */
export function transformAhrefsBrandRadarSovOverviewRow(
  raw: BrandRadarSovOverviewRowRaw,
  dataSource: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-sov-overview" satisfies AhrefsReportType,
    dataSource,
    brand: raw.brand ?? null,
    // Float 0-1 — DO NOT divide by 100.
    share_of_voice: raw.share_of_voice ?? null,
    // CamelCase mirror per dual-naming rule.
    shareOfVoice: raw.share_of_voice ?? null,
  };
}

export function transformAhrefsBrandRadarSovOverviewResponse(
  raw: ReadonlyArray<BrandRadarSovOverviewRowRaw>,
  dataSource: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarSovOverviewRow(row, dataSource)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/sov-history transformer
// ----------------------------------------------------------------------------
//
// Per-date row carries a nested `share_of_voice: Array<{brand, value}>`. The
// transformer preserves the nested array shape verbatim — flattening would
// force widget-render layers to re-aggregate, which is wasteful and lossy.

interface BrandRadarSovHistoryRowRaw {
  readonly date?: string | null;
  readonly share_of_voice?: ReadonlyArray<{
    readonly brand?: string | null;
    readonly value?: number | null;
  }> | null;
}

/**
 * Transform a single `/brand-radar/sov-history` row.
 *
 * **Nested-array preservation invariant (AC #1, catalogue row 209):**
 * `share_of_voice` is an array of `{brand, value}` objects per date. The
 * transformer preserves this nested shape verbatim — widgets render as a
 * stacked time-series with brand-as-color. DO NOT flatten into per-(date,brand)
 * rows.
 */
export function transformAhrefsBrandRadarSovHistoryRow(
  raw: BrandRadarSovHistoryRowRaw,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-sov-history" satisfies AhrefsReportType,
    dataSource,
    dateFrom,
    ...(dateTo ? { dateTo } : {}),
    date: raw.date ?? null,
    // Preserved nested array — DO NOT flatten.
    share_of_voice: raw.share_of_voice ?? null,
    // CamelCase mirror per dual-naming rule.
    shareOfVoice: raw.share_of_voice ?? null,
  };
}

export function transformAhrefsBrandRadarSovHistoryResponse(
  raw: ReadonlyArray<BrandRadarSovHistoryRowRaw>,
  dataSource: string,
  dateFrom: string,
  dateTo?: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarSovHistoryRow(row, dataSource, dateFrom, dateTo)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/cited-domains transformer
// ----------------------------------------------------------------------------
//
// 2026 deprecations: the `mentions` field (deprecated 2026-02-10) and `volume`
// field (deprecated 2026-03-24) are NULLed at ingest regardless of wire
// payload. Widgets that bind to these keys receive `null` mirrors so they can
// render "no data" rather than stale/obsolete values.

/* eslint-disable @typescript-eslint/naming-convention -- wire-format snake_case
   on this endpoint legitimately includes `mentions`/`volume` (both deprecated
   per the 2026 changelog). The transformer ALWAYS NULLs them at ingest so the
   wire field name is only meaningful for type safety on the raw row, not as a
   surfaced metric. The metric-allowlist guardrail is narrowed to context-aware
   per AC #5. Code Review Pass 3 (P3-L4) — disable/enable bracketing is
   verified scoped to the interface declaration only; runtime test coverage
   on transformer output catches regressions. */
interface BrandRadarCitedDomainRowRaw {
  readonly domain?: string | null;
  readonly pages?: number | null;
  readonly responses?: number | null;
  // The 2 deprecated fields below are present on the wire but NULLed at ingest.
  readonly mentions?: number | null;
  readonly volume?: number | null;
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Transform a single `/brand-radar/cited-domains` row.
 *
 * **2026 deprecation invariants (AC #5):**
 *   - `mentions` field deprecated 2026-02-10 (replacement: `responses`).
 *   - `volume` field deprecated 2026-03-24 (no replacement).
 * Both are explicitly NULLed regardless of wire payload — widgets binding to
 * these keys render "no data" rather than stale values.
 *
 * **dataSource semantics (Code Review Pass 3, P3-M2):** Ahrefs's cited-*
 * response is **aggregated** across the requested AI clients — the response
 * has no per-row data_source field. The `dataSource` argument here is the
 * comma-joined REQUESTED set (e.g. `"chatgpt,gemini,perplexity"`) and is
 * surfaced verbatim under both `dataSource` (label) and `requestedDataSources`
 * (array) so widgets filtering by AI client know whether they're looking at
 * a single-client view (`requestedDataSources.length === 1`) vs an aggregated
 * multi-client view.
 *
 * **Conservative wire-field forwarding (Code Review Pass 3, P3-L5):** the
 * transformer emits a hand-rolled object with only the documented fields
 * (`domain`, `pages`, `responses`, `mentions`, `volume`). Unknown wire
 * fields shipped by a future Ahrefs schema bump are dropped — this is
 * deliberate (deviates from Site Audit transformers in Story 20.2d which
 * spread the raw row). The cited-* surface has the deprecation invariant
 * that requires explicit field-by-field control; permissive `...raw`
 * spreading would risk re-introducing deprecated fields if Ahrefs's wire
 * format adds a new alias.
 */
export function transformAhrefsBrandRadarCitedDomainRow(
  raw: BrandRadarCitedDomainRowRaw,
  dataSource: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-cited-domains" satisfies AhrefsReportType,
    dataSource,
    requestedDataSources: dataSource.split(",").map((s) => s.trim()).filter(Boolean),
    domain: raw.domain ?? null,
    pages: raw.pages ?? null,
    responses: raw.responses ?? null,
    // 2 DEPRECATED fields — explicit null regardless of wire payload (AC #5).
    // Reading raw.mentions/raw.volume here would be a regression — the
    // transformer's contract is that these always emit null on cited-* rows.
    /* eslint-disable @typescript-eslint/naming-convention */
    mentions: null,
    volume: null,
    /* eslint-enable @typescript-eslint/naming-convention */
  };
}

export function transformAhrefsBrandRadarCitedDomainsResponse(
  raw: ReadonlyArray<BrandRadarCitedDomainRowRaw>,
  dataSource: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarCitedDomainRow(row, dataSource)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2e — /brand-radar/cited-pages transformer
// ----------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/naming-convention */
interface BrandRadarCitedPageRowRaw {
  readonly url?: string | null;
  readonly responses?: number | null;
  readonly mentions?: number | null;
  readonly volume?: number | null;
}
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * Transform a single `/brand-radar/cited-pages` row.
 *
 * Same 2026 deprecation invariants as cited-domains: `mentions` (2026-02-10)
 * and `volume` (2026-03-24) are NULLed at ingest. `tracked_urls` is a
 * REQUEST-only param (not a response field) so it does not appear here.
 *
 * **dataSource semantics (Code Review Pass 3, P3-M2):** see cited-domains
 * transformer. The `requestedDataSources` array distinguishes single-client
 * vs aggregated multi-client views.
 */
export function transformAhrefsBrandRadarCitedPageRow(
  raw: BrandRadarCitedPageRowRaw,
  dataSource: string
): Record<string, unknown> {
  return {
    _reportType: "brand-radar-cited-pages" satisfies AhrefsReportType,
    dataSource,
    requestedDataSources: dataSource.split(",").map((s) => s.trim()).filter(Boolean),
    url: raw.url ?? null,
    responses: raw.responses ?? null,
    /* eslint-disable @typescript-eslint/naming-convention */
    mentions: null,
    volume: null,
    /* eslint-enable @typescript-eslint/naming-convention */
  };
}

export function transformAhrefsBrandRadarCitedPagesResponse(
  raw: ReadonlyArray<BrandRadarCitedPageRowRaw>,
  dataSource: string
): Array<Record<string, unknown>> {
  return raw.map((row) =>
    transformAhrefsBrandRadarCitedPageRow(row, dataSource)
  );
}

// ----------------------------------------------------------------------------
// Story 20.2f — Web Analytics `/stats` + `/chart` transformers
// ----------------------------------------------------------------------------
//
// Both endpoints share the same 6 wire-format fields (pageviews, visitors,
// visits, session_bounce_rate, avg_session_length, avg_session_duration_sec).
// `/stats` returns a single aggregated object across the date range;
// `/chart` returns an array of per-timestamp points (ISO 8601 `timestamp`).
//
// **No synthetic camelCase mirrors:** the wire-format snake_case names are
// already single-token-readable (`pageviews`, `visitors`, etc.) so no
// camelCase aliasing is needed. Story 20.2d Site Audit set the precedent for
// "direct wire-format only" on FREE endpoints.
//
// **Null-handling invariant:** missing scalar fields emit `null` (NOT `0`).
// Story 20.2f explicitly forbids defensive defaulting to zero — the Story
// 20.3 widget UI renders `null` as `—` (em-dash) per the round-4 pre-mortem
// incident #3 prevention.

interface WebAnalyticsStatsRaw {
  readonly pageviews?: number | null;
  readonly visitors?: number | null;
  readonly visits?: number | null;
  readonly session_bounce_rate?: number | null;
  readonly avg_session_length?: number | null;
  readonly avg_session_duration_sec?: number | null;
}

/**
 * Transform a `/web-analytics/stats` response into a single
 * `AdapterMetrics.records[]` entry.
 *
 * Returns ONE record (not an array of length 1 — callers wrap into an array
 * if needed). `_reportType: "web-analytics-stats"`. The optional `dateRange`
 * argument is propagated as `{ from, to }` per-row metadata when the caller
 * supplies it (the proxy-route layer does; autonomous-sync does not).
 *
 * Null/undefined wire fields emit `null` (NOT `0`). Per round-3 hardening,
 * a missing `stats` object on the response (e.g. zero-data project) is
 * acceptable input — the transformer expects the helper to short-circuit
 * before reaching here, but defensively returns a record with all-null
 * scalars rather than throwing if called with `{}`.
 */
export function transformAhrefsWebAnalyticsStatsResponse(
  raw: WebAnalyticsStatsRaw | null | undefined,
  projectId: string | number,
  dateRange?: { readonly from?: string; readonly to?: string }
): Record<string, unknown> {
  const stats: WebAnalyticsStatsRaw = raw ?? {};
  return {
    _reportType: "web-analytics-stats" satisfies AhrefsReportType,
    projectId: String(projectId),
    ...(dateRange?.from || dateRange?.to
      ? {
          dateRange: {
            ...(dateRange.from ? { from: dateRange.from } : {}),
            ...(dateRange.to ? { to: dateRange.to } : {}),
          },
        }
      : {}),
    pageviews: stats.pageviews ?? null,
    visitors: stats.visitors ?? null,
    visits: stats.visits ?? null,
    session_bounce_rate: stats.session_bounce_rate ?? null,
    avg_session_length: stats.avg_session_length ?? null,
    avg_session_duration_sec: stats.avg_session_duration_sec ?? null,
  };
}

interface WebAnalyticsChartPointRaw {
  readonly timestamp?: string | null;
  readonly pageviews?: number | null;
  readonly visitors?: number | null;
  readonly visits?: number | null;
  readonly session_bounce_rate?: number | null;
  readonly avg_session_length?: number | null;
  readonly avg_session_duration_sec?: number | null;
}

/**
 * Transform a single `/web-analytics/chart` row into an
 * `AdapterMetrics.records[]` entry.
 *
 * `_reportType: "web-analytics-chart"`. `projectId` + `granularity` are
 * propagated as per-row metadata so widgets can group/dispatch on them.
 * `timestamp` is preserved verbatim (ISO 8601 string per docs).
 */
export function transformAhrefsWebAnalyticsChartRow(
  raw: WebAnalyticsChartPointRaw,
  projectId: string | number,
  granularity: string
): Record<string, unknown> {
  return {
    _reportType: "web-analytics-chart" satisfies AhrefsReportType,
    projectId: String(projectId),
    granularity,
    timestamp: raw.timestamp ?? null,
    pageviews: raw.pageviews ?? null,
    visitors: raw.visitors ?? null,
    visits: raw.visits ?? null,
    session_bounce_rate: raw.session_bounce_rate ?? null,
    avg_session_length: raw.avg_session_length ?? null,
    avg_session_duration_sec: raw.avg_session_duration_sec ?? null,
  };
}

/**
 * Transform a `/web-analytics/chart` `points` array into per-row records.
 *
 * **Null-array hardening (round-3 #3):** treats `null` AND `undefined` as
 * equivalent to `[]` (no data days). Prevents widget-render crashes on the
 * Story 20.3 timeline gauge — agencies refreshing a widget for a date range
 * with no WA ingest see "no data" instead of a runtime error. Matches the
 * `transformAhrefsKeywordsHistoryResponse` convention from Story 20.2.
 */
export function transformAhrefsWebAnalyticsChartResponse(
  raw: ReadonlyArray<WebAnalyticsChartPointRaw> | null | undefined,
  projectId: string | number,
  granularity: string
): Array<Record<string, unknown>> {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) =>
    transformAhrefsWebAnalyticsChartRow(row, projectId, granularity)
  );
}

// ----------------------------------------------------------------------------
// Story 35.8 — Subscription Info `/limits-and-usage` transformer
// ----------------------------------------------------------------------------
//
// Per [docs.ahrefs.com/api/reference/subscription-info/get-limits-and-usage]
// verified 2026-05-19:
//   - Endpoint accepts NO query params
//   - FREE (does not consume API units)
//   - Returns a singleton object with 7 fields
//
// Returns ONE record (not an array of length 1 — callers should not wrap).
// Null/undefined wire fields emit `null` (NOT `0`). The Story 20.4 widget
// renders null as `—` (em-dash), matching the Brand Radar SoV gauge + Web
// Analytics KPI conventions.

interface SubscriptionInfoLimitsAndUsageRaw {
  readonly subscription?: string | null;
  readonly usage_reset_date?: string | null;
  readonly units_limit_workspace?: number | null;
  readonly units_usage_workspace?: number | null;
  readonly units_limit_api_key?: number | null;
  readonly units_usage_api_key?: number | null;
  readonly api_key_expiration_date?: string | null;
}

/**
 * Transform a `/subscription-info/limits-and-usage` response into a single
 * `AdapterMetrics.records[]` entry.
 *
 * Returns ONE record (singleton; not an array of length 1). Sets
 * `_reportType: "subscription-info-limits-and-usage"` and preserves wire-format
 * snake_case fields verbatim.
 *
 * Null/undefined scalar fields emit `null` (NOT defensive `0` defaults — a
 * `null` unit counter is semantically different from `0` for the budget
 * gauge).
 *
 * Rejects non-object input (throws TypeError) matching the Story 20.2e/f
 * transformer convention.
 */
export function transformAhrefsSubscriptionInfoLimitsAndUsageResponse(
  raw: SubscriptionInfoLimitsAndUsageRaw | null | undefined
): Record<string, unknown> {
  if (raw !== null && raw !== undefined && typeof raw !== "object") {
    throw new TypeError(
      `transformAhrefsSubscriptionInfoLimitsAndUsageResponse: expected object | null | undefined, got ${typeof raw}`
    );
  }
  // Story 35.8 code-review pass 1 (M1) — defend against singleton-vs-array
  // wire drift (`typeof [] === "object"`). The endpoint docs guarantee a
  // singleton object response; if Ahrefs ever ships an array wrapper, we
  // want a loud throw rather than silent all-null emission that masks the
  // budget gauge.
  if (Array.isArray(raw)) {
    throw new TypeError(
      "transformAhrefsSubscriptionInfoLimitsAndUsageResponse: expected singleton object, got array — wire shape drift; check docs.ahrefs.com/api/reference/subscription-info/get-limits-and-usage"
    );
  }
  const data: SubscriptionInfoLimitsAndUsageRaw = raw ?? {};
  return {
    _reportType: "subscription-info-limits-and-usage" satisfies AhrefsReportType,
    subscription: data.subscription ?? null,
    usage_reset_date: data.usage_reset_date ?? null,
    units_limit_workspace: data.units_limit_workspace ?? null,
    units_usage_workspace: data.units_usage_workspace ?? null,
    units_limit_api_key: data.units_limit_api_key ?? null,
    units_usage_api_key: data.units_usage_api_key ?? null,
    api_key_expiration_date: data.api_key_expiration_date ?? null,
  };
}
